// FILE: PinnedUserMessage.logic.ts
// Purpose: Pure derivations for the user message pinned at the top of the transcript.
// Layer: Web chat presentation logic
// Exports: derivePinnedUserMessageText, resolvePinnedUserMessageIndex and their input contracts

import { splitPromptIntoDisplaySegments } from "~/composer-editor-mentions";
import {
  deriveDisplayedUserMessageState,
  IMAGE_ONLY_VISIBLE_PLACEHOLDER,
} from "~/lib/displayedUserMessage";
import { formatComposerSkillChipLabel } from "../composerInlineChip";
import {
  buildInlineTerminalContextText,
  textContainsInlineTerminalContextLabels,
} from "./userMessageTerminalContexts";

/**
 * Structural subset of a timeline user message. Kept minimal on purpose so the
 * derivation stays testable without building a whole `MessagesTimelineRow`.
 */
export interface PinnedUserMessageContent {
  text: string;
  // Only the kind is read here — a full `ChatAttachment` satisfies this, and
  // so does a fixture that carries nothing else.
  attachments?: ReadonlyArray<{ readonly type?: "image" | "file" }> | null | undefined;
}

function formatAttachmentSummary(
  attachments: ReadonlyArray<{ readonly type?: "image" | "file" }>,
): string {
  const count = attachments.length;
  // "images" only when they all are; a mixed message reads as files, which is
  // true of both kinds and wrong about neither.
  const noun = attachments.every((attachment) => (attachment.type ?? "image") === "image")
    ? "image"
    : "file";
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

/**
 * Flattens the chip-aware segments the bubble renders into plain text. The
 * pinned row is a single clamped line of text, so chips have to degrade to the
 * label the user would read on them rather than disappear.
 */
function flattenPromptSegmentsToText(text: string): string {
  return splitPromptIntoDisplaySegments(text)
    .map((segment) => {
      switch (segment.type) {
        case "text":
          return segment.text;
        case "skill":
          return formatComposerSkillChipLabel(segment.name);
        case "mention":
          return `@${segment.path}`;
        case "agent-mention":
          return `@${segment.alias}`;
        default:
          return "";
      }
    })
    .join("");
}

/**
 * Collapses newlines and runs of whitespace: the pinned row clamps to two
 * lines, and a message whose first two lines are blank would otherwise pin as
 * an empty box.
 */
function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Single-line text for the pinned row. Returns an empty string when the
 * message has nothing worth pinning, which callers treat as "render nothing".
 */
export function derivePinnedUserMessageText(message: PinnedUserMessageContent): string {
  const attachments = message.attachments ?? [];
  const attachmentCount = attachments.length;
  const displayed = deriveDisplayedUserMessageState(message.text, {
    hideImageOnlyBootstrapPrompt: attachmentCount > 0,
  });

  // Mirrors UserMessageBody: the labels are already inline in the text for
  // newer messages, and only need to be prefixed for the ones that predate it.
  const contexts = displayed.contexts;
  const contextPrefix =
    contexts.length > 0 && !textContainsInlineTerminalContextLabels(displayed.visibleText, contexts)
      ? buildInlineTerminalContextText(contexts)
      : "";

  // An image-only message renders a "(No Content)" placeholder inside the
  // bubble, where the thumbnails sit right next to it. The pinned row has no
  // thumbnails, so that placeholder would read as a bug: name the images.
  const isImageOnly =
    attachmentCount > 0 && displayed.visibleText.trim() === IMAGE_ONLY_VISIBLE_PLACEHOLDER;
  const body = isImageOnly
    ? formatAttachmentSummary(attachments)
    : collapseWhitespace(flattenPromptSegmentsToText(displayed.visibleText));
  const withContexts = collapseWhitespace(`${contextPrefix} ${body}`);

  if (withContexts.length > 0) {
    return withContexts;
  }

  return attachmentCount > 0 ? formatAttachmentSummary(attachments) : "";
}

/** A user-message row that could be pinned, with its measured position. */
export interface PinnedUserMessageCandidate {
  /** Index of the row inside the timeline row list. */
  rowIndex: number;
  /**
   * Top edge of the row in pixels, relative to the top edge of the scroll
   * viewport. Negative means the row already scrolled past the top.
   */
  viewportTopPx: number;
}

/**
 * Picks the user message whose turn the reader is currently inside: the last
 * one whose top edge has already left the viewport through the top.
 *
 * A message that merely *crosses* the top edge (its top is still on screen, its
 * body runs below) is not pinned — the pin would sit right on top of the row it
 * duplicates. Returns null when every user message is still on screen.
 *
 * Candidates must be ordered by row index, which is also top-to-bottom order.
 */
export function resolvePinnedUserMessageIndex(
  candidates: ReadonlyArray<PinnedUserMessageCandidate>,
): number | null {
  let pinnedRowIndex: number | null = null;
  // Deliberately a full scan instead of stopping at the first on-screen row:
  // rows whose height is still an estimate can momentarily report a top that
  // breaks the monotonic ordering, and the list is one entry per turn anyway.
  for (const candidate of candidates) {
    if (candidate.viewportTopPx >= 0) continue;
    if (pinnedRowIndex === null || candidate.rowIndex > pinnedRowIndex) {
      pinnedRowIndex = candidate.rowIndex;
    }
  }
  return pinnedRowIndex;
}

/** The subset of `window` the frame scheduler needs, so tests can fake it. */
export interface FrameSchedulerHost {
  requestAnimationFrame: (callback: () => void) => number;
  cancelAnimationFrame: (frame: number) => void;
}

export interface FrameScheduler {
  /** Queues `run` for the next frame. A second call before it fires is a no-op. */
  schedule: (run: () => void) => void;
  /** Drops the queued frame, if any. */
  cancel: () => void;
}

/**
 * Coalesces repeated work into one animation frame.
 *
 * The invariant that matters — and that a hand-rolled `pendingFrameRef` got
 * wrong — is that **cancelling has to release the slot**. Cancelling the frame
 * while leaving its id behind makes the slot look permanently occupied, and
 * every later `schedule` silently does nothing. React StrictMode walks straight
 * into that: it mounts, runs effects, tears them down and mounts again, so the
 * teardown cancels the frame the first pass queued and the second pass can
 * never queue another one.
 */
export function createFrameScheduler(host: FrameSchedulerHost): FrameScheduler {
  let pendingFrame: number | null = null;

  return {
    schedule(run) {
      if (pendingFrame !== null) return;
      pendingFrame = host.requestAnimationFrame(() => {
        pendingFrame = null;
        run();
      });
    },
    cancel() {
      if (pendingFrame === null) return;
      const frame = pendingFrame;
      // Release the slot first: a cancelled frame must never keep it taken.
      pendingFrame = null;
      host.cancelAnimationFrame(frame);
    },
  };
}
